import { chooseCreatedRecord, isTableRow } from "../lib/createCompletion";
import type { CreateCompletedFrame } from "./readCreateCompleted";

/** Longest wait for the first create of a run. */
export const CREATE_WATCH_TIMEOUT_MS = 30_000;

/**
 * Quiet period after a create that is not a row of the launching table. A run
 * writes its host records first, so another frame may still be on its way.
 */
export const CREATE_SETTLE_MS = 1_500;

export interface WatchCreateOptions {
  correlationId: string;
  /** Resource of the table the action sits on. */
  tableResource: string;
  timeoutMs?: number;
  settleMs?: number;
}

export interface CreateWatch {
  /** The created record the launching table should open. */
  result: Promise<CreateCompletedFrame>;
  /**
   * Starts the timeout. Call once the run is accepted: the watch is opened
   * before submit, and a sign-in popup can hold the submit for minutes.
   */
  arm: () => void;
  cancel: () => void;
}

export type WatchCreate = (options: WatchCreateOptions) => CreateWatch;

/**
 * Collects every create frame of one run and settles on the one to open.
 *
 * A row of the launching table settles at once. Anything else waits for the
 * run to go quiet, then {@link chooseCreatedRecord} picks from all of them.
 */
export class CreateCollector {
  readonly correlationId: string;
  readonly result: Promise<CreateCompletedFrame>;

  private readonly tableResource: string;
  private readonly settleMs: number;
  private readonly timeoutMs: number;
  private readonly frames: CreateCompletedFrame[] = [];
  private readonly onDone: () => void;
  private resolve!: (frame: CreateCompletedFrame) => void;
  private reject!: (error: Error) => void;
  private timeoutTimer: ReturnType<typeof setTimeout> | undefined;
  private settleTimer: ReturnType<typeof setTimeout> | undefined;
  private done = false;

  constructor(options: WatchCreateOptions, onDone: () => void) {
    this.correlationId = options.correlationId;
    this.tableResource = options.tableResource;
    this.settleMs = options.settleMs ?? CREATE_SETTLE_MS;
    this.timeoutMs = options.timeoutMs ?? CREATE_WATCH_TIMEOUT_MS;
    this.onDone = onDone;
    this.result = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    // A cancelled watch nobody awaits must not surface as an unhandled rejection.
    this.result.catch(() => undefined);
  }

  /** Starts the timeout. Frames that arrived earlier are kept. */
  arm(): void {
    if (this.done || this.timeoutTimer) {
      return;
    }

    this.timeoutTimer = setTimeout(() => this.settle(), this.timeoutMs);
  }

  /** Adds a frame of this run. Frames of other runs are ignored. */
  add(frame: CreateCompletedFrame): void {
    if (this.done || frame.correlationId !== this.correlationId) {
      return;
    }

    if (this.frames.some((seen) => seen.primaryKey === frame.primaryKey)) {
      return;
    }

    this.frames.push(frame);
    console.warn("[create-completion] create frame", {
      correlationId: frame.correlationId,
      resource: frame.resource,
      tableResource: this.tableResource,
    });

    if (isTableRow(frame, this.tableResource)) {
      this.finish(frame);
      return;
    }

    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.settle(), this.settleMs);
  }

  cancel(): void {
    this.finish(undefined, new Error("The create watch was cancelled."));
  }

  private settle(): void {
    const chosen = chooseCreatedRecord(this.frames, this.tableResource);

    this.finish(
      chosen,
      chosen ? undefined : new Error("Timed out waiting for the created record."),
    );
  }

  private finish(frame: CreateCompletedFrame | undefined, error?: Error): void {
    if (this.done) {
      return;
    }

    this.done = true;
    clearTimeout(this.timeoutTimer);
    clearTimeout(this.settleTimer);
    this.onDone();

    if (frame) {
      this.resolve(frame);
      return;
    }

    this.reject(error ?? new Error("Timed out waiting for the created record."));
  }
}
